import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import MediaSubjectPicker, { MediaSubjectItem } from 'modules/championship/ui/components/form-parts/media/media-subject-picker';
import MediaSubjectPickerSkeleton from 'modules/championship/ui/components/form-parts/media/media-subject-picker-skeleton';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import { SkeletonPreview } from 'ud-ui/components/skeleton';
import UDText from 'ud-ui/components/text';

interface ChangeTemplateGroupModalProps {
  /** Наборы шаблонов организации турнира. */
  groups: MediaManagerTemplateGroup[];
  isLoading: boolean;
  /** Набор, назначенный сейчас: выбран заранее, и «Назначить» на нём неактивна. */
  currentGroupId: number;
  /** Назначает набор; `true` — успех. Ошибку показывает вызывающая сторона, окно остаётся открытым. */
  onAssign: (templateGroupId: number) => Promise<boolean>;
  onClose: () => void;
}

/**
 * Окно «Сменить набор шаблонов» (IMGM-1512).
 *
 * Раньше «Сменить» превращало карточку набора в заглушку на всю ширину, а наборы стояли ниже
 * строками, неотличимыми от «Типов медиа». Теперь карточка остаётся на месте, а выбор — в окне:
 * радиокнопки + «Отмена» / «Назначить» — диалог подтверждения из Material Design, где выбор можно
 * менять сколько угодно раз до подтверждения. Окно — форма по образцу «Сгенерировать»
 * (`generate-media-modal.tsx`): Enter на выбранной радиокнопке отправляет её.
 */
const ChangeTemplateGroupModal = ({ groups, isLoading, currentGroupId, onAssign, onClose }: ChangeTemplateGroupModalProps) => {
  const { t } = useLingui();
  const [selectedId, setSelectedId] = useState<number>(currentGroupId);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const items: MediaSubjectItem[] = useMemo(
    () =>
      groups.map((group) => ({
        id: group.id,
        title: group.name,
        meta: plural(group.templatesCount, { one: '# шаблон', few: '# шаблона', many: '# шаблонов', other: '# шаблонов' }),
      })),
    [groups],
  );

  const hasOtherGroups = groups.some((group) => group.id !== currentGroupId);

  // Пока идёт назначение, окно не закрывается ни «Отменой», ни Escape, ни кликом мимо:
  // иначе ответ пришёл бы на экран, который пользователь уже считает отменённым.
  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || selectedId === currentGroupId) return;
    setIsSubmitting(true);
    const isAssigned = await onAssign(selectedId);
    setIsSubmitting(false);
    if (isAssigned) onClose();
  };

  const title = t`Сменить набор шаблонов`;

  return (
    <Modal show onClose={handleClose} size="md">
      <Modal.Header
        title={title}
        subtitle={t`Набор — это группа шаблонов из медиа-менеджера. Турнир будет генерировать картинки по шаблонам выбранного.`}
      />
      <form onSubmit={(event) => void handleSubmit(event)} noValidate aria-label={title}>
        <Modal.Body>
          <div className="flex flex-col gap-3" data-testid="change-template-group-modal">
            {/* Список монтируется только с данными: `MediaSubjectPicker` замеряет высоту при первом показе. */}
            {isLoading ? (
              <MediaSubjectPickerSkeleton subjectType="stage" withMeta />
            ) : (
              <SkeletonPreview skeleton={<MediaSubjectPickerSkeleton subjectType="stage" withMeta />}>
                <MediaSubjectPicker
                  items={items}
                  value={selectedId}
                  onChange={setSelectedId}
                  label={t`Наборы шаблонов организации`}
                  searchPlaceholder={t`Название набора`}
                />
              </SkeletonPreview>
            )}
            {/* Без подсказки окно с одним выбранным пунктом и неактивной кнопкой выглядит сломанным. */}
            {!isLoading && !hasOtherGroups ? (
              <UDText type="footnote" className="text-inkSecondary" data-testid="change-template-group-no-others">
                <Trans>
                  Других наборов у организации нет.{' '}
                  <Link to={Pages.MEDIA_MANAGER.INDEX} className="text-inkPrimary font-semibold underline">
                    Создать в медиа-менеджере
                  </Link>
                </Trans>
              </UDText>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <UDButton variant="secondary" type="button" onClick={handleClose} disabled={isSubmitting}>
            <Trans>Отмена</Trans>
          </UDButton>
          <UDButton
            variant="primary"
            type="submit"
            loading={isSubmitting}
            disabled={selectedId === currentGroupId}
            data-testid="change-template-group-submit"
          >
            <Trans>Назначить</Trans>
          </UDButton>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default ChangeTemplateGroupModal;
